import { FormattedMessage, defineMessages, useIntl } from 'react-intl';

import FeedbackCollector from '@atlaskit/feedback-collector';

import { AppDataContext } from 'src/app/data';

interface UnlinkedWorkspaceFeedbackCollectorInterface {
  onCancel: () => void;
  onSubmit: () => void;
}

export const urls = {
  privacyPolicy: 'https://www.atlassian.com/legal/privacy-policy',
  atlassianResearchGroup: 'https://www.atlassian.com/research-group',
  contactSupportLink:
    'https://support.atlassian.com/contact/#/?inquiry_category=technical_issues',
};

const messages = defineMessages({
  giveFeedbackTitle: {
    id: 'global.components.unlinkedWSFeedbackCollector.giveFeedbackTitle',
    description: 'Text for button to open feedback modal',
    defaultMessage: 'Give feedback',
  },
  giveFeedbackPlaceholder: {
    id: 'global.components.unlinkedWSFeedbackCollector.giveFeedbackPlaceholder',
    description: 'Text for placeholder in give feedback modal',
    defaultMessage:
      'Workspace: [Workspace ID]\n\nPlease describe what is preventing you from linking:\n\nDo you want our team to reach out to help you complete this step? (y/n)',
  },
  giveFeedbackTitleDetailsOne: {
    id: 'global.components.unlinkedWSFeedbackCollector.giveFeedbackTitleDetailsOne',
    description: 'Text for title details in give feedback modal',
    defaultMessage:
      'Your thoughts are valuable in helping improve our products',
  },
  giveFeedbackTitleDetailsTwo: {
    id: 'global.components.unlinkedWSFeedbackCollector.giveFeedbackTitleDetailsTwo',
    description:
      'Text for title details in give feedback modal. The "support site" link text is wrapped in <link> tags.',
    defaultMessage:
      "If you're looking to get help or want to report a bug, visit our <link>support site.</link>",
  },
  canContactLabel: {
    id: 'global.components.unlinkedWSFeedbackCollector.canContactLabel',
    description:
      'Text for the contact label. The "privacy policy" link text is wrapped in <link> tags.',
    defaultMessage:
      'Atlassian can contact me about this feedback. See our <link>privacy policy</link>',
  },
  enrollLabel: {
    id: 'global.components.unlinkedWSFeedbackCollector.enrollLabel',
    description:
      'Text for the enroll label. The "Atlassian Research Group" link text is wrapped in <link> tags.',
    defaultMessage:
      "I'd like to help improve Atlassian products by joining the <link>Atlassian Research Group</link>",
  },
});

export const UnlinkedWorkspaceFeedbackCollector = ({
  onCancel,
  onSubmit,
}: UnlinkedWorkspaceFeedbackCollectorInterface): JSX.Element => {
  const intl = useIntl();

  // Entrypoint will be the same as nav feedback & theme collector (feedback collectors team rec'd this)
  const entrypointId = '3e0d44da-9d2b-43b6-9119-e17b876ee1b4';

  return (
    <AppDataContext.Consumer>
      {appData => (
        <FeedbackCollector
          locale={intl.locale}
          entrypointId={entrypointId}
          atlassianAccountId={appData?.user?.aaid || undefined}
          onClose={onCancel}
          onSubmit={onSubmit}
          timeoutOnSubmit={0} // Don't wait to tell parent that feedback was submitted so we can re-hide the page-block modal immediately
          additionalFields={[
            {
              // "Feedback Context" custom field that's used across all feedback collectors in BBC codebase
              id: 'customfield_10047',
              value: `URL: ${window.location.href}\nUser Agent: ${window.navigator.userAgent}\nlabel: org-linking`,
            },
          ]}
          showTypeField={false}
          feedbackTitle={intl.formatMessage(messages.giveFeedbackTitle)}
          feedbackTitleDetails={
            <>
              <FormattedMessage
                {...messages.giveFeedbackTitleDetailsOne}
                tagName="p"
              />
              <FormattedMessage
                {...messages.giveFeedbackTitleDetailsTwo}
                values={{
                  link: chunks => (
                    <a
                      href={urls.contactSupportLink}
                      target="_blank"
                      rel="noopener"
                    >
                      {chunks}
                    </a>
                  ),
                }}
                tagName="p"
              />
            </>
          }
          canBeContactedLabel={
            <FormattedMessage
              {...messages.canContactLabel}
              values={{
                link: chunks => (
                  <a href={urls.privacyPolicy} target="_blank" rel="noopener">
                    {chunks}
                  </a>
                ),
              }}
              tagName="p"
            />
          }
          enrolInResearchLabel={
            <FormattedMessage
              {...messages.enrollLabel}
              values={{
                link: chunks => (
                  <a
                    href={urls.atlassianResearchGroup}
                    target="_blank"
                    rel="noopener"
                  >
                    {chunks}
                  </a>
                ),
              }}
              tagName="p"
            />
          }
          summaryPlaceholder={intl.formatMessage(
            messages.giveFeedbackPlaceholder
          )}
        />
      )}
    </AppDataContext.Consumer>
  );
};
